<template>
    <v-layout row wrap align-center>

        <v-flex style="width: 120px; max-width: 120px; flex: 0 0 120px;">
            <v-select v-model="selectedCountry" :items="filteredCountries" item-text="name" return-object dense
                hide-details :disabled="disabled" :menu-props="{ maxHeight: 300, lazy: true }"
                no-data-text="Ülke bulunamadı">
                <template v-slot:prepend-item>
                    <v-card flat class="px-2 py-1" style="position: sticky; top: 0; z-index: 2; background: white;">
                        <v-text-field v-model="countrySearch" placeholder="Ülke veya Kod ara... (TR, +90)" dense
                            hide-details single-line clearable @click.native.stop @keydown.native.stop />
                    </v-card>
                    <v-divider />
                </template>

                <template v-slot:selection="{ item }">
                    <div class="d-flex align-center" style="gap: 8px;">
                        <span :class="flagClass(item.iso2)"></span>
                        <span class="body-2 font-weight-bold">{{ item.dialCode }}</span>
                    </div>
                </template>

                <template v-slot:item="{ item }">
                    <v-list-tile-content>
                        <div class="d-flex align-center" style="gap: 10px; width: 100%;">
                            <span :class="flagClass(item.iso2)"></span>
                            <span style="min-width: 55px;" class="font-weight-bold">{{ item.dialCode }}</span>
                            <span class="text-truncate">{{ item.name }}</span>
                        </div>
                    </v-list-tile-content>
                </template>
            </v-select>
        </v-flex>

        <v-flex style="flex: 1 1 auto; min-width: 0;" class="pl-sm-2 pt-xs-2 pt-sm-0">
            <v-text-field v-model="displayPhone" :label="label" :rules="rules"  dense hide-details="false"
                type="tel" :placeholder="placeholder" :disabled="disabled" @input="onDisplayInput" @blur="onBlur" />
        </v-flex>

    </v-layout>
</template>

<script>
import metadata from "libphonenumber-js/metadata.min.json";
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";

export default {
    name: "PhoneInput",
    props: {
        value: { type: String, default: "" }, // Örn: +905334413747 (E.164 Format)
        label: { type: String, default: "Telefon" },
        locale: { type: String, default: "tr-TR" },
        rules: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false }
    },
    data() {
        return {
            countries: [],
            selectedCountry: null,
            displayPhone: "",
            countrySearch: "",
            lastE164: "",
            isInternalChange: false // Sonsuz döngüyü (watcher loop) engellemek için flag
        };
    },
    computed: {
        placeholder() {
            // Dinamik placeholder; TR için standart kalıp, diğerleri için boş veya jenerik
            return this.selectedCountry && this.selectedCountry.iso2 === "TR"
                ? "(5xx) xxx-xxxx"
                : "";
        },
        filteredCountries() {
            const q = (this.countrySearch || "").toLowerCase().trim();
            if (!q) return this.countries;

            return this.countries.filter(c => {
                const name = (c.name || "").toLowerCase();
                const iso2 = (c.iso2 || "").toLowerCase();
                const dial = (c.dialCode || "").toLowerCase();
                // Arama yaparken kullanıcının yazdığı "+" işaretini tolere et
                const cleanQ = q.startsWith("+") ? q.substring(1) : q;
                const cleanDial = dial.startsWith("+") ? dial.substring(1) : dial;

                return name.includes(q) || iso2.includes(q) || cleanDial.includes(cleanQ);
            });
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                // Eğer değişiklik bileşenin içinden tetiklendiyse dışarıdan gelen değeri ezme
                if (this.isInternalChange) {
                    this.isInternalChange = false;
                    return;
                }
                this.setFromE164(newVal);
            }
        },
        selectedCountry(newVal, oldVal) {
            if (oldVal && newVal && oldVal.iso2 !== newVal.iso2) {
                // Ülke değiştiğinde mevcut inputu yeni ülkeye göre tekrar formatla
                this.onDisplayInput(this.displayPhone);
            }
        }
    },
    created() {
        this.countries = this.buildCountriesFromLibPhone();
        // Varsayılan ülke TR, bulunamazsa listenin ilki
        if (!this.selectedCountry) {
            this.selectedCountry = this.findCountryByIso2("TR") || this.countries[0];
        }
    },
    methods: {
        buildCountriesFromLibPhone() {
            let dn = null;
            try {
                if (typeof Intl !== "undefined" && Intl.DisplayNames) {
                    dn = new Intl.DisplayNames([this.locale], { type: "region" });
                }
            } catch (e) {
                console.warn("Intl.DisplayNames desteklenmiyor, ISO kodları kullanılacak.");
            }

            const ccc = metadata.country_calling_codes || {};
            const iso2ToDial = {};

            Object.keys(ccc).forEach(dialDigits => {
                const arr = ccc[dialDigits] || [];
                arr.forEach(iso2 => {
                    const upIso = String(iso2 || "").toUpperCase();
                    if (upIso) iso2ToDial[upIso] = dialDigits;
                });
            });

            const iso2List = Object.keys(metadata.countries || {}).sort();
            const list = [];

            iso2List.forEach(iso2 => {
                const dial = iso2ToDial[iso2];
                if (!dial) return;

                const name = dn ? dn.of(iso2) : iso2;
                list.push({
                    iso2: iso2,
                    name: name,
                    dialCode: "+" + dial
                });
            });

            // Türkiye'yi en başa taşı, gerisini alfabetik sırala
            const turkey = list.find(c => c.iso2 === "TR");
            const others = list.filter(c => c.iso2 !== "TR");

            others.sort((a, b) => (a.name || "").localeCompare(b.name || "", this.locale));

            return turkey ? [turkey, ...others] : others;
        },

        findCountryByIso2(iso2) {
            const up = String(iso2 || "").toUpperCase();
            return this.countries.find(c => c.iso2 === up) || null;
        },

        flagClass(iso2) {
            // world-flag-icons veya flag-icon-css kütüphaneleriyle uyumlu class üretimi
            return ["flag-icon", `flag-icon-${String(iso2 || "").toLowerCase()}`];
        },

        cleanDigits(s) {
            return String(s || "").replace(/\D/g, "");
        },

        onDisplayInput(val) {
            const iso2 = this.selectedCountry ? this.selectedCountry.iso2 : "TR";

            // Türkiye seçiliyken kullanıcı ilk karakter olarak 0 girerse onu sil
            if (iso2 === "TR" && val && val.startsWith("0")) {
                val = val.substring(1);
            }

            // Kullanıcının yazdığı değerden dynamic olarak format oluşturma
            const ayt = new AsYouType(iso2);
            this.displayPhone = ayt.input(val || "");

            this.processAndEmit();
        },

        onBlur() {
            this.normalizeIfPossible();
            this.$emit("change", this.lastE164);
        },

        processAndEmit() {
            const iso2 = this.selectedCountry ? this.selectedCountry.iso2 : "TR";
            const dialCode = this.selectedCountry ? this.selectedCountry.dialCode : "+90";

            // Kullanıcı input alanına ülke kodunu manuel yazdıysa temizle (çift kod olmasın diye)
            let nationalDigits = this.cleanDigits(this.displayPhone);
            const cleanDial = this.cleanDigits(dialCode);

            if (nationalDigits.startsWith(cleanDial)) {
                nationalDigits = nationalDigits.substring(cleanDial.length);
            }

            const candidate = dialCode + nationalDigits;
            const parsed = parsePhoneNumberFromString(candidate, iso2);

            if (parsed && parsed.isValid()) {
                this.lastE164 = parsed.number;
                this.isInternalChange = true;
                this.$emit("input", parsed.number);
            } else {
                // Geçersiz/eksik/fazla numarada veriyi kaybetmemek için kullanıcının yazdığı değeri olduğu gibi gönder
                this.lastE164 = candidate;
                this.isInternalChange = true;
                this.$emit("input", candidate);
            }
        },

        normalizeIfPossible() {
            const iso2 = this.selectedCountry ? this.selectedCountry.iso2 : "TR";
            const dialCode = this.selectedCountry ? this.selectedCountry.dialCode : "+90";
            let nationalDigits = this.cleanDigits(this.displayPhone);
            const cleanDial = this.cleanDigits(dialCode);

            if (nationalDigits.startsWith(cleanDial)) {
                nationalDigits = nationalDigits.substring(cleanDial.length);
            }

            const candidate = dialCode + nationalDigits;
            const parsed = parsePhoneNumberFromString(candidate, iso2);

            if (parsed && parsed.isValid()) {
                // GÖRÜNTÜ İÇİN: Formatı al, TR ise baştaki sıfırı sil
                let formatted = parsed.formatNational();
                if (iso2 === "TR" && formatted.startsWith("0")) {
                    formatted = formatted.replace(/^0\s*/, "");
                }

                this.displayPhone = formatted; // Ekranda 53x... görünür
                this.lastE164 = parsed.number;
                this.isInternalChange = true;
                this.$emit("input", parsed.number); // Parent'a +90... gider
            }
        },

        setFromE164(e164) {
            if (!e164) {
                this.displayPhone = "";
                this.lastE164 = "";
                return;
            }

            const parsed = parsePhoneNumberFromString(e164);
            if (parsed) {
                let iso2 = parsed.country || "TR";
                if (parsed.country) {
                    const found = this.findCountryByIso2(parsed.country);
                    if (found) {
                        this.selectedCountry = found;
                        iso2 = found.iso2;
                    }
                }

                // GÖRÜNTÜ İÇİN: Dışarıdan gelen veriyi formatlarken TR ise sıfırı uçur
                let formatted = parsed.formatNational();
                if (iso2 === "TR" && formatted.startsWith("0")) {
                    formatted = formatted.replace(/^0\s*/, "");
                }
                this.displayPhone = formatted;
                this.lastE164 = parsed.number;
            } else {
                // Eğer gelen değer ham/geçersiz bir format ise direkt basmayı dene
                this.displayPhone = e164;
                this.lastE164 = "";
            }
        }
    }
};
</script>

<style scoped>
/* Vuetify 1.x flex gap emülasyonu ve bayrak görünümü için ufak dokunuşlar */
.d-flex {
    display: flex !important;
}

.align-center {
    align-items: center !important;
}

.text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Bayrak kütüphanesi yoksa kırılmasın diye varsayılan placeholder box */
.flag-icon {
    width: 22px !important;
    min-width: 22px !important;
    max-width: 22px !important;
    height: 14px !important;
    flex: 0 0 22px;
    display: inline-block;
    background-color: #eee;
    background-size: cover;
    background-position: center;
    border-radius: 2px;
}
</style>